/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { Alert } from '@material-ui/lab';
import React from 'react';
import { Element } from 'react-scroll';
import Typography from '@material-ui/core/Typography';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import { Info } from '@material-ui/icons';
import {merge} from 'lodash';
import { FeeCategories } from '../../draft/fees/FeeCategories';

const useStyles = customStyle =>
  makeStyles((theme) => {
    const styles = {
      root: {
        width: '100%',
      },
      note: {
        width: '100%',
        marginTop: '2em',
        marginBottom: '2em',
      },
      alert: {
        fontSize: '1rem',
      },
      infoIcon: {
        color: theme.palette.info.main,
      },
      section: {
        visibility: 'visible',
        opacity: 1,
        transition: 'visibility 0s, opacity 0.5s linear',
      },
      sectionHidden: {
        visibility: 'hidden',
        opacity: 0,
        height: 0,
        transition: 'visibility 0s, opacity 0.5s linear',
      },
    };

    merge(styles, customStyle);

    return styles;
  });

/**
 * Custom RJSF field that holds the fees selection table.
 *
 * Allows the following options on uiSchema:
 *  ui:title": section title
 *  ui:optional": optional title component
 *  ui:description": subtitle description,
 *  ui:note": section info under the title
 */
export default function FeesSelectionField(props) {
  const { uiSchema, idSchema, idPrefix } = props;
  const classes = useStyles(uiSchema?.['ui:options']?.customStyle)();
  const { t } = useTranslation();
  const isSectionHidden = props.uiSchema['ui:hidden'] && idPrefix === 'submission';

  return (
    <div className={classNames(isSectionHidden && classes.sectionHidden, classes.section)}>
      <Element name={idSchema.$id} />

      <Typography variant="h4" component="span" aria-label={props.uiSchema['ui:feesTitle']}>
        {props.uiSchema['ui:feesTitle']}
        {props.schema?.optional && <span>{props.uiSchema['ui:optional']}</span>}
      </Typography>

      {props.uiSchema['ui:note'] && (
        <div className={classes.note}>
          <Alert
            className={classes.alert}
            severity="info"
            variant="outlined"
            icon={<Info className={classes.infoIcon} />}
          >
            {props.uiSchema['ui:note']}
          </Alert>
        </div>
      )}

      {props.uiSchema['ui:enableRenewalFee'] && (
        <div className={classes.root}>
          <Alert severity="warning">
            {`${t('fees.renewal-fee.warning', { amount: props.uiSchema['ui:renewalFeeAmount'] })}`}
          </Alert>
        </div>
      )}

      <FeeCategories {...props} />
    </div>
  );
}
